/**
 * One candidate on the Outreach tab.
 *
 * The send path is three human steps and the tool takes none of them for the recruiter:
 *  1. "Write InMail" copies the reviewed text and opens THEIR Recruiter composer;
 *  2. they paste and press Send in LinkedIn;
 *  3. back here, "I sent it" records it (two-step confirm, so a stray click cannot).
 */
import { useState, type ReactNode } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { cn } from '../../../../shared';
import * as api from '../../services/taOutreachApi';
import type { OutreachCandidate, OutreachConfig } from '../../types/taOutreach';
import { OutreachDraftEditor } from './OutreachDraftEditor';
import { FollowUpEditor } from './FollowUpEditor';
import { profileLinkOf } from './profileLink';
import { OutreachEvidence } from './OutreachEvidence';
import { OutreachSentPanel } from './OutreachSentPanel';
import { canSendInProject, explain, extensionVersion, runInExtension } from './outreachExtension';
import { composerUrl, copyAndOpenComposer } from './outreachOpen';
import { DraftWhy } from './DraftWhy';
import { editsReplacedBy, REDRAFT_WHY, signForMe, whyNotSigned } from './redraftWhy';
import { SentAsNote } from './SentAsNote';
import { AutoApproveNote, SenderName } from './outreachUi';

/** The pay words the server's `stripPay` removes; the button shows only when the text has one. */
// Keep in step with PAY_WORDS + OTHER_PAY_WORDS in proxy-server/lib/outreachDraft.js (CodeRabbit, PR #663).
const MENTIONS_PAY =
  /\b(\w*gehalt\w*|salary|vergütung|compensation|brutto|gross|eur|bonus\w*|prämie\w*|provision|commission|equity|vsop|aktienoptionen|stock options?|virtual shares)\b|€/i;

const STATUS_LABEL: Record<OutreachCandidate['status'], { text: string; className: string }> = {
  shortlisted: { text: 'Shortlisted', className: 'bg-[#E9EAEC] text-[#707070]' },
  imported: { text: 'New', className: 'bg-[#E9EAEC] text-[#1A1A1A]' },
  analysed: { text: 'Checked', className: 'bg-[#FCEEE2] text-[#B96716]' },
  queued: { text: 'Waiting for draft', className: 'bg-[#FCEEE2] text-[#B96716]' },
  drafted: { text: 'Draft to review', className: 'bg-[#DBEBFC] text-[#0072EA]' },
  ready: { text: 'Approved', className: 'bg-[#DEF5E6] text-[#1D9247]' },
  sent: { text: 'Sent', className: 'bg-[#DFF3E4] text-[#1B7F3B]' },
  dropped: { text: 'Dropped', className: 'bg-[#F8F8F8] text-[#707070]' },
};

export function OutreachCard({
  candidate,
  config,
  expanded,
  drafting,
  onToggle,
  onDraft,
  onUpdated,
  select,
}: {
  candidate: OutreachCandidate;
  config: OutreachConfig;
  expanded: boolean;
  drafting: boolean;
  onToggle: () => void;
  /** Redraft; with `template`, using that library template for this person (null = the role's). */
  onDraft: (template?: { scope: 'company' | 'personal'; key: string } | null) => void;
  onUpdated: (item: OutreachCandidate) => void;
  select?: ReactNode; // the tick for "Approve N" (Sofia, Oct 28 2026), before the name
}) {
  const c = candidate;
  const [text, setText] = useState({ subject: c.draft?.subject || '', body: c.draft?.body || '' });
  const [opened, setOpened] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const dirty = text.subject !== (c.draft?.subject || '') || text.body !== (c.draft?.body || '');
  const closed = c.status === 'sent' || c.status === 'dropped';

  // A new draft from the server replaces the editor text. Adjusted during render (React's
  // "storing information from previous renders" pattern) rather than in an effect.
  const draftKey = `${c.draft?.generatedAt || ''}|${c.draft?.subject || ''}|${c.draft?.body || ''}`;
  const [syncedKey, setSyncedKey] = useState(draftKey);
  const [redraftWhy, setRedraftWhy] = useState<string | null>(null);
  // A colleague's draft that could not be put together for the viewer on Approve or send (Oct 28 2026).
  const [notSigned, setNotSigned] = useState<string | null>(null);
  // A hand-edited draft put together as the viewer on Approve or send: whose edits were replaced
  // (one more look first; CodeRabbit, PR #753, Sofia, Oct 28 2026).
  const [resigned, setResigned] = useState<string | null>(null);
  if (syncedKey !== draftKey) {
    setSyncedKey(draftKey);
    setText({ subject: c.draft?.subject || '', body: c.draft?.body || '' });
    setRedraftWhy(null);
    setNotSigned(null);
  }
  const [now] = useState(() => Date.now());

  /**
   * Another template: put the parts already written into it (instant, no model). Only a draft from
   * before the parts were stored is written again (Sofia, Oct 22 2026: "must be there within a second").
   */
  const [switching, setSwitching] = useState(false);
  async function pickTemplate(t: { scope: 'company' | 'personal'; key: string } | null) {
    setSwitching(true);
    try {
      const r = await api.applyOutreachTemplate(c.id, t);
      if (r.item) onUpdated(r.item);
      else if (r.refusal) showToast({ status: 'error', position: 'bottomCenter', description: `Not switched: ${r.refusal}` });
      else {
        setRedraftWhy(r.reason ? REDRAFT_WHY[r.reason] || null : null);
        onDraft(t);
      }
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not switch the template.' });
    } finally {
      setSwitching(false);
    }
  }

  async function run<T>(fn: () => Promise<T>, onDone: (r: T) => void, failMsg: string) {
    setBusy(true);
    try {
      onDone(await fn());
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : failMsg });
    } finally {
      setBusy(false);
    }
  }

  /**
   * Saves the editor text; false when it did not save OR the guards refused it (a refusal is a 200
   * that still stores the text with a warning). Approve and "Write InMail" go on only on true, so
   * neither can carry on with the old draft or with text the guards turned down.
   */
  async function save(): Promise<boolean> {
    setBusy(true);
    try {
      const r = await api.saveOutreachDraft(c.id, text.subject, text.body);
      onUpdated(r.item);
      if (r.refusal) {
        showToast({ status: 'error', position: 'bottomCenter', description: `Not approved: ${r.refusal}` });
        return false;
      }
      return true;
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not save your edit.' });
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** Approve; a colleague's draft is put together for the viewer first, on the server (Oct 28 2026). */
  async function approve() {
    if (dirty && !(await save())) return;
    setBusy(true);
    setResigned(null);
    try {
      const r = await api.moveOutreach(c.id, 'ready');
      onUpdated(r.item);
      setResigned(r.resigned ? editsReplacedBy(c) : null);
    } catch (err) {
      const body = err instanceof api.OutreachApiError ? err.body : {};
      if (body.code === 'sent_as') setNotSigned(whyNotSigned(body.reason as string | null));
      else showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not approve.' });
    } finally {
      setBusy(false);
    }
  }

  async function writeInMail() {
    // Only the approved text goes out; an edit needs "Save & approve" first (CodeRabbit, PR #624).
    if (dirty || c.status !== 'ready') return;
    // Written for another sender or caller, or with an older version: "Update" first (CodeRabbit, PR #725).
    if (c.sentAsStale || c.draftOutdated || notSigned) return;
    let body = text.body;
    // A colleague's draft: signed for the viewer first, never copied as someone else's (Oct 28 2026).
    if (c.sentAsOther) {
      const r = await signForMe(c.id);
      if ('why' in r) return setNotSigned(r.why);
      onUpdated(r.item);
      // A new text nobody approved yet: back to "drafted", shown once more, not copied.
      if (r.item.status !== 'ready') return setResigned(editsReplacedBy(c));
      body = r.item.draft?.body || '';
    }
    // Body only: LinkedIn has a separate subject field, filled via "Copy subject" on the editor.
    const r = await copyAndOpenComposer(c, '', body);
    setOpened(true);
    void logAnalyticsEvent('ta_outreach_composer_opened', { direct: r.direct, opened: r.opened, copied: r.copied });
    showToast({
      status: r.opened ? 'success' : 'info',
      description: !r.copied
        ? 'Could not copy automatically. Select the text and copy it yourself.'
        : r.opened
          ? r.direct
            ? 'Message copied. Paste it into the composer that just opened, then use “Copy subject” for the Betreff field.'
            : 'Message copied. In the tab that opened: “View in Recruiter” → envelope → paste. Then “Copy subject” for the Betreff field.'
          : 'Copied, but the browser blocked the new tab. Allow pop-ups for this page or open the profile yourself.',
    });
  }

  const markSent = () =>
    run(
      () => api.markOutreachSent(c.id, text.subject, text.body),
      (r) => {
        onUpdated(r.item);
        setConfirming(false);
        void logAnalyticsEvent('ta_outreach_marked_sent', { lever: r.item.lever?.status || 'none', test_mode: r.testMode });
        showToast({ status: 'success', description: r.testMode ? 'Recorded here (test mode). Lever is only read.' : 'Recorded as sent. Lever is checked for the exported profile.' });
      },
      'Could not record that.'
    );

  const toggleFact = (factId: string, include: boolean) => {
    const current = new Set(c.enrichment?.excludedFactIds || []);
    if (include) current.delete(factId);
    else current.add(factId);
    void run(() => api.setExcludedFacts(c.id, [...current]), (r) => onUpdated(r.item), 'Could not save that.');
  };

  /** One click either way (Theresa, Oct 20 2026); adding back writes the InMail again with the range in it. */
  const removeSalary = () =>
    run(
      () => api.setOutreachSalary(c.id, false, text),
      (r) => {
        onUpdated(r.item);
        if (r.refusal) showToast({ status: 'error', position: 'bottomCenter', description: `Not approved: ${r.refusal}` });
      },
      'Could not remove the salary.'
    );
  const addSalaryBack = () =>
    run(
      () => api.setOutreachSalary(c.id, true),
      (r) => {
        onUpdated(r.item);
        onDraft();
      },
      'Could not add the salary back.'
    );
  const mentionsPay = MENTIONS_PAY.test(text.body);

  const status = STATUS_LABEL[c.status];
  const due = c.followUp && !c.followUp.closedAt && Date.parse(c.followUp.dueAt) < now;
  return (
    <div id={`ta-outreach-${c.id}`} className="rounded-md border border-solid border-[#E9EAEC] bg-white">
      {/* The name opens the LinkedIn profile; a click ANYWHERE else on the row opens the card (Sofia, Oct 19 2026). */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Close' : 'Open'} ${c.name}`}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
          e.preventDefault();
          onToggle();
        }}
        className="flex w-full cursor-pointer items-start justify-between gap-3 px-4 py-3"
      >
        {select}
        <span className="min-w-0 flex-1">
          {profileLinkOf(c) ? (
            <a
              href={profileLinkOf(c) as string}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-block font-semibold text-[#1A1A1A] underline"
              title="Open the LinkedIn profile"
            >
              {c.name}
            </a>
          ) : (
            <span className="inline-block font-semibold text-[#1A1A1A]">{c.name}</span>
          )}{' '}
          <SenderName draft={c.draft} owner={c.owner} />
          <span className="block max-w-[560px] text-[13px] leading-snug text-[#707070]">
            {[c.headline, c.company, c.location].filter(Boolean).join(' · ') || '—'}
          </span><AutoApproveNote c={c} />
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {c.reply && <span className="rounded-full bg-[#DEF5E6] px-2 py-0.5 text-xs font-medium text-[#1D9247]">Replied</span>}
          {due && !c.reply && <span className="rounded-full bg-[#FDE7E7] px-2 py-0.5 text-xs font-medium text-[#B3261E]">Follow-up due</span>}
          {!c.recruiterId && !closed && (
            <span className="rounded-full bg-[#F8F8F8] px-2 py-0.5 text-xs text-[#707070]" title="No Recruiter id: the button opens the public profile (two extra clicks).">
              no Recruiter id
            </span>
          )}
          <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', status.className)}>{drafting ? 'Drafting…' : status.text}</span>
        </span>
      </div>

      {expanded && (
        <div className="grid gap-4 border-t border-solid border-[#E9EAEC] px-4 py-3 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <OutreachEvidence candidate={c} onToggleFact={toggleFact} busy={busy || closed} />
          <div className="space-y-3">
            {c.status === 'dropped' ? (
              <p className="text-[13px] text-[#707070]">Dropped. Profile text and draft were removed.</p>
            ) : !c.draft && c.status !== 'sent' ? (
              <div className="space-y-2 text-[13px]">
                {c.draftProblem && <p className="text-[#B3261E]">No draft: {c.draftProblem} Write this one yourself, or try again.</p>}
                <Button variant="primary" size="sm" onClick={() => onDraft()} loading={drafting} disabled={drafting}>
                  {c.status === 'imported' ? 'Draft InMail' : 'Try again'}
                </Button>
              </div>
            ) : (
              <>
                {c.status !== 'sent' && (
                  <DraftWhy candidate={c} drafting={drafting || switching} why={redraftWhy} disabled={closed || busy || drafting || switching} onPick={(t) => void pickTemplate(t)} />
                )}
                {/* Who signs it: own draft for another sender, older version, a colleague's draft (Oct 26–28 2026). */}
                {!closed && (
                  <SentAsNote
                    c={c}
                    edited={dirty || c.draft?.source === 'edited'}
                    why={notSigned}
                    resigned={c.status === 'drafted' ? resigned : null}
                    disabled={busy || switching || drafting}
                    onReassemble={() => void pickTemplate(c.inmailTemplate ?? null)}
                    onRedraft={() => onDraft()}
                  />
                )}
                {/* Common ground (PR 7, Oct 27 2026): what the opener names, and where it is in their profile. */}
                {c.draft?.commonGround && c.status !== 'sent' && (
                  <p className="m-0 text-xs text-[#707070]" title={`Their profile: “${c.draft.commonGround.quote}”`} data-testid="common-ground">
                    Common ground: <b className="font-semibold text-[#1A1A1A]">{c.draft.commonGround.label}</b>
                  </p>
                )}
                {/* While a redraft runs the old text stays visible but greyed out (Sofia, Oct 22 2026). */}
                <div className={drafting ? 'pointer-events-none opacity-40 transition-opacity' : 'transition-opacity'} aria-busy={drafting}>
                <OutreachDraftEditor
                  candidateId={c.id}
                  subject={text.subject}
                  body={text.body}
                  warnings={c.draft?.warnings || []}
                  // An approved InMail stays editable, ✨ included, like the follow-up (Sofia, Oct 19 2026):
                  // a change takes the approval back until "Save & approve".
                  disabled={closed || busy}
                  onChange={setText}
                  maxBody={config.maxBodyChars}
                  maxSubject={config.maxSubjectChars}
                />
                </div>
                {!closed && <FollowUpEditor candidate={c} subject={text.subject} onUpdated={onUpdated} />}
                {!closed && (
                  <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                    {/* Left: what moves the draft on. Right: leaving it. Same corner as the cards. */}
                    <span className="flex flex-wrap items-center gap-2">
                      {(c.status === 'drafted' || (c.status === 'ready' && dirty)) && (
                        <Button
                          variant="primary"
                          size="sm"
                          className="rounded-md"
                          disabled={busy || !text.body.trim() || Boolean(c.sentAsStale || c.draftOutdated || notSigned)}
                          onClick={() => void approve()}
                        >
                          {dirty ? 'Save & approve' : 'Approve'}
                        </Button>
                      )}
                      {c.status === 'ready' && !dirty && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-md"
                          disabled={busy}
                          onClick={() => run(() => api.moveOutreach(c.id, 'unready'), (r) => onUpdated(r.item), 'Could not reopen.')}
                        >
                          Undo approval
                        </Button>
                      )}
                      {dirty && (
                        <Button variant="outline" size="sm" className="rounded-md" onClick={() => void save()} disabled={busy}>
                          Save edit
                        </Button>
                      )}
                      {c.status !== 'ready' && (
                        <Button variant="outline" size="sm" className="rounded-md" onClick={() => onDraft()} disabled={drafting || busy}>
                          Redraft
                        </Button>
                      )}
                      {c.noSalary ? (
                        <Button variant="ghost" size="sm" className="rounded-md" onClick={() => void addSalaryBack()} disabled={drafting || busy}>
                          Add salary back
                        </Button>
                      ) : (
                        mentionsPay && (
                          <Button variant="ghost" size="sm" className="rounded-md" onClick={() => void removeSalary()} disabled={drafting || busy}>
                            Remove salary
                          </Button>
                        )
                      )}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-md text-[#707070]"
                      disabled={busy}
                      onClick={() =>
                        run(() => api.moveOutreach(c.id, 'skip'), (r) => {
                          onUpdated(r.item);
                          void logAnalyticsEvent('ta_outreach_dropped', {});
                        }, 'Could not skip this candidate.')
                      }
                    >
                      Skip candidate
                    </Button>
                    {c.status === 'ready' && !config.testMode && (
                      <>
                        <span className="basis-full text-xs text-[#707070]">
                          First, in Recruiter: <b>Export to Lever</b> (one click on the profile). Lever then registers the InMail by itself. A
                          reply it does not notice: click “They replied” here, or export the profile again.
                        </span>
                        {/* Changed text needs "Save & approve" first: nothing unapproved reaches LinkedIn (CodeRabbit, PR #624). */}
                        <Button variant="secondary" size="sm" onClick={writeInMail} disabled={!composerUrl(c) || busy || dirty || Boolean(c.sentAsStale || c.draftOutdated || notSigned)}>
                          Write InMail in LinkedIn ↗
                        </Button>
                        {!confirming ? (
                          <Button variant="secondary" size="sm" onClick={() => setConfirming(true)} disabled={busy || !opened}>
                            I sent it
                          </Button>
                        ) : (
                          <span className="flex items-center gap-2 text-[13px]">
                            Sent in LinkedIn?
                            <Button variant="primary" size="sm" onClick={markSent} loading={busy}>
                              Yes, record it
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                              Not yet
                            </Button>
                          </span>
                        )}
                      </>
                    )}
                  </div>
                )}
                {c.status === 'ready' && config.testMode && (
                  <div className="flex flex-wrap items-center gap-2 text-xs text-[#707070]">
                    <span>Approved — it goes out with “Start” in the Send box below.</span>
                    {/* Test mode: records "sent" in the proxy's memory only, so the Lever read-back can be tried. */}
                    <Button variant="ghost" size="sm" className="rounded-md" onClick={markSent} loading={busy}>
                      Mark as sent (test — records it here only)
                    </Button>
                  </div>
                )}
                {c.status === 'sent' && (
                  <OutreachSentPanel
                    candidate={c}
                    busy={busy}
                    onCopyFollowUp={async () => {
                      // With the extension: it types the follow-up into the composer and leaves the
                      // tab open for the recruiter to read and send. Without it: copy + open, as before.
                      const version = await extensionVersion();
                      if (c.recruiterId && version) {
                        const r = await runInExtension({
                          url: composerUrl(c, { inProject: canSendInProject(version) }) as string,
                          name: c.name,
                          subject: c.draft?.subject ? `Re: ${c.draft.subject}`.slice(0, 80) : '',
                          body: c.followUp?.text || '',
                          send: false,
                          followUpFill: true,
                        });
                        showToast(
                          r.ok
                            ? { status: 'success', description: 'Follow-up typed into LinkedIn. Check it in the tab and press Send there.' }
                            : { status: 'error', position: 'bottomCenter', description: explain(r) }
                        );
                        return;
                      }
                      const r = await copyAndOpenComposer(c, '', c.followUp?.text || '');
                      showToast({ status: 'info', description: r.copied ? 'Follow-up copied.' : 'Copy the text yourself.' });
                    }}
                    onFollowUpSent={() =>
                      run(() => api.markFollowUpSent(c.id), (r) => {
                        onUpdated(r.item);
                        void logAnalyticsEvent('ta_outreach_follow_up_sent', {});
                      }, 'Could not save that.')
                    }
                    onCloseFollowUp={(reason) =>
                      run(() => api.closeFollowUp(c.id, reason), (r) => {
                        onUpdated(r.item);
                        void logAnalyticsEvent('ta_outreach_follow_up_closed', { reason });
                      }, 'Could not save that.')
                    }
                  />
                )}
                {c.status === 'sent' && config.testMode && c.testRecipient && (
                  <div className="flex flex-wrap items-center gap-2 text-xs text-[#707070]">
                    <span>Test recipient: you can send this InMail again, to see LinkedIn’s “already contacted” being caught.</span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-md"
                      loading={busy}
                      onClick={() => run(() => api.moveOutreach(c.id, 'resend_test'), (r) => onUpdated(r.item), 'Could not put it back in the queue.')}
                    >
                      Send again (test)
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
